/*
 * Оформление карточек шаблонов в разделе «Создать» по spec.type шаблона: цвет типа для
 * светлой и тёмной тем и знак технологии (штурвал Kubernetes, слон PostgreSQL, знак Kafka;
 * у модели и сервиса — иконки соответствующих разделов сайдбара). Неизвестный тип получает
 * нейтральное оформление.
 */
import { ComponentType } from 'react';
import { SvgIconProps } from '@material-ui/core';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import ExtensionIcon from '@material-ui/icons/Extension';
import {
  AirflowIcon,
  DefectDojoIcon,
  FeastIcon,
  GitlabIcon,
  KafkaIcon,
  KubernetesIcon,
  MlModelIcon,
  MongodbIcon,
  PostgresIcon,
  RabbitmqIcon,
  ServiceIcon,
  ValkeyIcon,
  CloudIcon,
} from '../components/icons';

export type TemplateLook = {
  /** Цвет типа: плашка иконки и водяной знак. */
  color: { light: string; dark: string };
  /** Заливка шапки: пастель в светлой теме, тонировка цвета типа в тёмной. */
  tint: { light: string; dark: string };
  Icon: ComponentType<SvgIconProps>;
};

const LOOKS: Record<string, TemplateLook> = {
  'kubernetes-cluster': {
    color: { light: '#326ce5', dark: '#3d7ff0' },
    tint: { light: '#e4ecfb', dark: 'rgba(61,127,240,.18)' },
    Icon: KubernetesIcon,
  },
  'ml-model': {
    color: { light: '#7c3aed', dark: '#8b5cf6' },
    tint: { light: '#eee7fb', dark: 'rgba(139,92,246,.18)' },
    Icon: MlModelIcon,
  },
  'app-service': {
    color: { light: '#0d9488', dark: '#14b8a6' },
    tint: { light: '#dff3f0', dark: 'rgba(20,184,166,.18)' },
    Icon: ServiceIcon,
  },
  'feature-store': {
    color: { light: '#d97706', dark: '#f59e0b' },
    tint: { light: '#fbeedb', dark: 'rgba(245,158,11,.18)' },
    Icon: FeastIcon,
  },
  airflow: {
    color: { light: '#0f9488', dark: '#17b3a3' },
    tint: { light: '#dcf3f0', dark: 'rgba(23,179,163,.18)' },
    Icon: AirflowIcon,
  },
  // хосты в Yandex Cloud для kubespray — штурвал, как у кластера
  'yc-hosts': {
    color: { light: '#1d4ed8', dark: '#60a5fa' },
    tint: { light: '#dbe6fb', dark: 'rgba(96,165,250,.18)' },
    Icon: KubernetesIcon,
  },
  // DefectDojo: реестр уязвимостей платформы — щит
  defectdojo: {
    color: { light: '#7c3aed', dark: '#a78bfa' },
    tint: { light: '#ede9fe', dark: 'rgba(167,139,250,.18)' },
    Icon: DefectDojoIcon,
  },
  // GitLab платформы: шаблон «Обновление GitLab платформы» — оранжевый, знак GitLab
  gitlab: {
    color: { light: '#e24329', dark: '#fc6d26' },
    tint: { light: '#fde8e1', dark: 'rgba(252,109,38,.18)' },
    Icon: GitlabIcon,
  },
  'kafka-cluster': {
    color: { light: '#3f4a5c', dark: '#64748b' },
    tint: { light: '#e7eaef', dark: 'rgba(100,116,139,.22)' },
    Icon: KafkaIcon,
  },
  'postgres-cluster': {
    color: { light: '#336791', dark: '#4a86b8' },
    tint: { light: '#e2ebf3', dark: 'rgba(74,134,184,.2)' },
    Icon: PostgresIcon,
  },
  'mongodb-cluster': {
    color: { light: '#3f8a3a', dark: '#47a248' },
    tint: { light: '#e4f2e3', dark: 'rgba(71,162,72,.2)' },
    Icon: MongodbIcon,
  },
  'rabbitmq-cluster': {
    color: { light: '#d95500', dark: '#ff6600' },
    tint: { light: '#ffeadb', dark: 'rgba(255,102,0,.18)' },
    Icon: RabbitmqIcon,
  },
  'valkey-cluster': {
    color: { light: '#4a5fd6', dark: '#6983ff' },
    tint: { light: '#e6eafd', dark: 'rgba(105,131,255,.2)' },
    Icon: ValkeyIcon,
  },
  // managed-хранилища cloud.ru: знак технологии, голубая гамма облака (MySQL — облако)
  'managed-postgres': {
    color: { light: '#0e7490', dark: '#22b8cf' },
    tint: { light: '#e0f4f8', dark: 'rgba(34,184,207,.18)' },
    Icon: PostgresIcon,
  },
  'managed-mysql': {
    color: { light: '#0e7490', dark: '#22b8cf' },
    tint: { light: '#e0f4f8', dark: 'rgba(34,184,207,.18)' },
    Icon: CloudIcon,
  },
  'managed-redis': {
    color: { light: '#0e7490', dark: '#22b8cf' },
    tint: { light: '#e0f4f8', dark: 'rgba(34,184,207,.18)' },
    Icon: ValkeyIcon,
  },
  'managed-rabbitmq': {
    color: { light: '#0e7490', dark: '#22b8cf' },
    tint: { light: '#e0f4f8', dark: 'rgba(34,184,207,.18)' },
    Icon: RabbitmqIcon,
  },
  // managed-базы VK Cloud: знак технологии, синяя гамма VK (MySQL — облако)
  'vkcloud-postgres': {
    color: { light: '#0057d9', dark: '#3d8bff' },
    tint: { light: '#e3edff', dark: 'rgba(61,139,255,.18)' },
    Icon: PostgresIcon,
  },
  'vkcloud-mysql': {
    color: { light: '#0057d9', dark: '#3d8bff' },
    tint: { light: '#e3edff', dark: 'rgba(61,139,255,.18)' },
    Icon: CloudIcon,
  },
  'vkcloud-redis': {
    color: { light: '#0057d9', dark: '#3d8bff' },
    tint: { light: '#e3edff', dark: 'rgba(61,139,255,.18)' },
    Icon: ValkeyIcon,
  },
  // «Удалить сервис»: тип service, тэг delete — красный, корзина
  service: {
    color: { light: '#b91c1c', dark: '#dc2626' },
    tint: { light: '#fbe5e5', dark: 'rgba(220,38,38,.18)' },
    Icon: DeleteOutlineIcon,
  },
};

const DEFAULT_LOOK: TemplateLook = {
  color: { light: '#4f46e5', dark: '#5eead4' },
  tint: { light: '#eceafd', dark: 'rgba(94,234,212,.14)' },
  Icon: ExtensionIcon,
};

export const templateLook = (type?: string): TemplateLook =>
  (type && LOOKS[type]) || DEFAULT_LOOK;
